import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useScript } from 'usehooks-ts';
import { Modal } from '@grandvisionhq/common';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useVto } from '../../../context/VtoProvider.js';
import { vtoEvent } from '../../../utils/vto-event.js';
import { initializationParams } from '../../config/initOptions.js';
import { pushVirtualMirrorDataLayerEvent } from '../../utils/events.js';

const VirtualMirrorLoader = ({ variants , onToggleWishlist , wishlistedSkuItems , transitionFeature  })=>{
    const { isVtoAddToWishlistEnabled  } = useFeatureToggles();
    const { setVtoActive , setVmApp , config: config1  } = useVto();
    const { apiKey , environment , script , termsAndConditions  } = config1;
    const { locale , region  } = useIntl();
    const privacyPolicyDataLayerEvent = 'privacy_policy_click';
    const status = useScript(script, {
        removeOnUnmount: false
    });
    // Since we don't want to re-init the VM script inside the useEffect,
    // we cannot pass any deps to the useEffect. However, if we don't pass
    // onToggleWishlist to the deps, this function will always have stale data
    // inside it. By using a ref to save the latest reference of the function,
    // we can bypass useEffect "caching" of the function without using deps.
    const onToggleWishlistRef = React.useRef();
    onToggleWishlistRef.current = onToggleWishlist;
    React.useEffect(()=>{
        const onToggleWishlistHandler = isVtoAddToWishlistEnabled ? (product)=>{
            if (onToggleWishlistRef.current) {
                return onToggleWishlistRef.current(product.upc);
            }
            return Promise.reject();
        } : undefined;
        if (status === 'ready' && typeof window.vmmv !== 'undefined') {
            try {
                const DEFAULT_INIT_PARAMS = initializationParams(apiKey, environment, locale, region, termsAndConditions);
                const config = swcHelpers.objectSpread({}, DEFAULT_INIT_PARAMS, variants, {
                    onToggleWishlist: onToggleWishlistHandler,
                    wishlistedUpcs: wishlistedSkuItems !== null && wishlistedSkuItems !== void 0 ? wishlistedSkuItems : [],
                    transitionFeature,
                    onClose: ()=>{
                        setVtoActive(false);
                    },
                    onPrivacyAgree: ()=>{
                        pushVirtualMirrorDataLayerEvent({
                            event: privacyPolicyDataLayerEvent,
                            interactionType: 'continue_click'
                        });
                    },
                    onPrivacyReject: ()=>{
                        pushVirtualMirrorDataLayerEvent({
                            event: privacyPolicyDataLayerEvent,
                            interactionType: 'cancel_click'
                        });
                        setVtoActive(false);
                    }
                });
                const vmAppInstance = new window.vmmv.VMWidgetApp();
                vmAppInstance.setConfig(config).then(()=>{
                    setVmApp(vmAppInstance);
                    vmAppInstance.init();
                }).catch((error)=>{
                    console.error(error);
                    setVtoActive(false);
                });
            } catch (error) {
                console.error(error);
                setVtoActive(false);
            }
        }
    // reason: VM script only needs to be set up once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        status
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "virtual-mirror__target"
    });
};
const VirtualMirrorModal = ({ variants , onToggleWishlist , wishlistedSkuItems , transitionFeature  })=>{
    const { vmApp , setVtoActive , setVmApp  } = useVto();
    React.useEffect(()=>{
        var ref, ref1;
        // user has the option to switch frames inside VM,
        // but we don't know that in advance, so we just send
        // the first sku to datalayer.
        const sku = variants === null || variants === void 0 ? void 0 : (ref = variants.products) === null || ref === void 0 ? void 0 : (ref1 = ref[0]) === null || ref1 === void 0 ? void 0 : ref1.upc;
        if (sku) {
            vtoEvent('vto.tryon.open', {
                sku
            });
        }
        return ()=>{
            if (sku) {
                vtoEvent('vto.close', {
                    sku
                });
            }
        };
    }, [
        variants === null || variants === void 0 ? void 0 : variants.products
    ]);
    if (!variants) {
        return null;
    }
    const onModalClose = function() {
        var _ref = swcHelpers.asyncToGenerator(function*() {
            if (vmApp) {
                yield vmApp.unmount();
                setVmApp(null);
            }
            setVtoActive(false);
        });
        return function onModalClose() {
            return _ref.apply(this, arguments);
        };
    }();
    return /*#__PURE__*/ React.createElement("div", {
        className: "virtual-mirror",
        "data-t": "virtual-mirror"
    }, /*#__PURE__*/ React.createElement(Modal, {
        activeTrap: false,
        "data-t": "virtual-mirror-modal",
        isOpen: true,
        className: "virtual-mirror__modal",
        onModalClose: onModalClose
    }, /*#__PURE__*/ React.createElement(VirtualMirrorLoader, {
        variants: variants,
        onToggleWishlist: onToggleWishlist,
        wishlistedSkuItems: wishlistedSkuItems,
        transitionFeature: transitionFeature
    })));
};

export { VirtualMirrorLoader, VirtualMirrorModal };
//# sourceMappingURL=VirtualMirrorModal.js.map
